/**
 * "What if?": the next years of net worth, month by month, as things are and
 * with the change the sliders describe (see `whatIf` in shared/compute.js).
 *
 * One model, two layouts. The desktop one keeps the sliders in a column beside
 * the figures and answers a hover with a tooltip; the phone puts the result on
 * top of the chart instead -- a finger covers whatever a tooltip would show --
 * and lists the chart's markers under it rather than labelling them in place.
 */

import { h } from '../vdom.js';
import { currentMonth, shiftMonth, toBase } from '../../../shared/model.js';
import { whatIf } from '../../../shared/compute.js';
import { C, MONO, css } from './common.js';

/** The three starting points: overpayment, investing, and the assumed return. */
export const WHAT_IF_PRESETS = [
  { extra: 300, invest: 200, ret: 5 },
  { extra: 600, invest: 400, ret: 7 },
  { extra: 1200, invest: 800, ret: 10 }
];

/* Colours the palette has no name for: the cash bar, and the warning's reds. Shared with the overview. */
export const CASH = '#8FA3BA';
export const DANGER_TEXT = '#FF8A8A';
export const DANGER_BODY = '#C9A9AE';
export const DANGER_BG = 'rgba(239,78,67,.08)';
export const DANGER_LINE = 'rgba(239,78,67,.38)';
export const GAIN_BG = 'rgba(61,217,160,.07)';
export const GAIN_LINE = '#1E4A3B';
export const GAIN_LABEL = '#6FBFA2';

/**
 * The simulation for the sliders as set -- the panel and the overview's card
 * read the same one. It starts from today's balances whichever month is on
 * screen (`metricsNow`): the future does not change with the month picked.
 */
export function whatIfFor(state, metrics) {
  const w = state.whatIf;
  if (state.metricsNow) metrics = state.metricsNow;
  const totals = metrics.totals;
  return whatIf({
    cash: totals.cash,
    investments: totals.invValue,
    /*
     * In the base currency, as the cash and income beside them are: a euro
     * loan went in as that many złoty and the whole projection undercounted
     * it. A debt with no rate is left out, as the totals leave it out.
     */
    debts: (metrics.debtRows || []).map(row => {
      const base = metrics.baseCurrency;
      const inBase = value => toBase(value, row.currency || base, base, metrics.rates || {});
      const balance = inBase(row.balance);
      const payment = inBase(row.scheduledPayment);
      return balance === null || payment === null ? null : { balance, rate: row.rate, payment };
    }).filter(Boolean),
    monthlySaving: metrics.runway.monthlyIncome - metrics.runway.monthlyOutgoings,
    extraDebt: w.extra, invest: w.invest, annualReturn: w.ret, years: w.years
  });
}

const clamp = (value, low, high) => Math.min(high, Math.max(low, value));
const pct = ratio => (ratio * 100).toFixed(2) + '%';

export function whatIfPanel({ state, metrics, t, fmt, actions, compact = false }) {
  const s = t.shell;
  if (!metrics?.runway || metrics.isEmpty) return null;
  const w = state.whatIf;
  const result = whatIfFor(state, metrics);
  const { baseline: base, scenario: scen, months } = result;
  const changed = w.extra > 0 || w.invest > 0;
  const hasDebt = (metrics.debtRows || []).some(row => row.balance > 0);

  const money = value => fmt.money(Math.round(value));
  const signed = value => fmt.signed(Math.round(value));
  const monthOf = m => fmt.monthName(shiftMonth(currentMonth(), m));
  const debtFree = m => (m === null ? s.wiBeyond : m === 0 ? s.wiNoDebt : monthOf(m));
  const years = s.wiYearsValue(w.years);
  const end = { scen: scen.end.netWorth, base: base.end.netWorth };
  const gain = result.gain;

  // ------------------------------------------------------------- scale ---
  const values = [...base.monthly.map(p => p.netWorth), ...scen.monthly.map(p => p.netWorth), 0];
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const rough = (hi - lo) / (compact ? 3 : 4) || 1000;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const step = [1, 2, 2.5, 5, 10].map(x => x * magnitude).find(x => x >= rough);
  /*
   * A dip just below zero gets a sliver of room, not a whole band: a step of
   * 200 thousand under a low of minus four thousand would squash the rest.
   */
  const yMin = lo < 0 && -lo < step * 0.25 ? lo - step * 0.08 : Math.floor(lo / step) * step;
  const yMax = Math.max(Math.ceil(hi / step) * step, yMin + step);
  const X = m => (m / months) * 1000;
  const Y = v => ((yMax - v) / (yMax - yMin)) * 300;
  const top = v => pct((yMax - v) / (yMax - yMin));
  const left = m => pct(m / months);

  /* "60 tys." / "60k": a short axis, the unit in the reader's language. */
  const axis = v => {
    if (Math.abs(v) < 0.001) return '0';
    const big = Math.abs(v) >= 1e6;
    const n = Math.abs(v) / (big ? 1e6 : 1e3);
    const decimals = Number.isInteger(n) ? 0 : Number.isInteger(Math.round(n * 1000) / 100) ? 1 : 2;
    return (v < 0 ? '−' : '') + fmt.number(n, decimals) + (big ? s.wiAxisM : s.wiAxisK);
  };
  const grid = [];
  for (let v = yMax; v >= yMin - step / 1000; v -= step) grid.push(Math.abs(v) < step / 1000 ? 0 : v);

  const line = series => 'M' + series.map(p => X(p.month).toFixed(1) + ',' + Y(p.netWorth).toFixed(1)).join('L');
  const band = line(scen.monthly) + 'L'
    + base.monthly.slice().reverse().map(p => X(p.month).toFixed(1) + ',' + Y(p.netWorth).toFixed(1)).join('L') + 'Z';

  const svg = h('svg', {
    key: 'svg',
    viewBox: '0 0 1000 300', preserveAspectRatio: 'none',
    style: css({ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', overflow: 'visible' })
  },
    changed && h('path', { key: 'band', d: band, fill: gain >= 0 ? 'rgba(61,217,160,.14)' : 'rgba(239,78,67,.14)', stroke: 'none' }),
    h('path', {
      key: 'base', d: line(base.monthly), fill: 'none', stroke: C.muted, 'stroke-width': 2,
      'stroke-dasharray': changed ? '6 6' : null, 'vector-effect': 'non-scaling-stroke', 'stroke-linejoin': 'round'
    }),
    changed && h('path', {
      key: 'scen', d: line(scen.monthly), fill: 'none', stroke: C.accent, 'stroke-width': compact ? 2.5 : 3,
      'vector-effect': 'non-scaling-stroke', 'stroke-linejoin': 'round', 'stroke-linecap': 'round'
    })
  );

  // ----------------------------------------------------------- markers ---
  const markers = [];
  if (changed && scen.debtFree > 0) {
    markers.push({ m: scen.debtFree, color: C.accent, bg: GAIN_BG, fg: C.accentHi, tag: s.wiTagDebt(monthOf(scen.debtFree)), name: s.wiMarkDebt });
  }
  if (base.debtFree > 0 && (!changed || base.debtFree !== scen.debtFree)) {
    markers.push({
      m: base.debtFree, color: C.muted, bg: C.card, fg: C.textDim,
      tag: changed ? s.wiTagDebtNow(monthOf(base.debtFree)) : s.wiTagDebt(monthOf(base.debtFree)),
      name: changed ? s.wiMarkDebtNow : s.wiMarkDebt
    });
  }
  const shortAt = scen.short;
  if (shortAt !== null) {
    markers.push({ m: shortAt, color: C.danger, bg: DANGER_BG, fg: DANGER_TEXT, tag: s.wiTagShort(monthOf(shortAt)), name: s.wiMarkShort });
  }

  // ------------------------------------------------------------- hover ---
  const hover = state.whatIfHover !== null && state.whatIfHover <= months ? state.whatIfHover : null;
  const pick = event => {
    const box = event.currentTarget.getBoundingClientRect();
    if (!box.width) return;
    actions.setWhatIfHover(Math.round(clamp((event.clientX - box.left) / box.width, 0, 1) * months));
  };
  const leave = () => actions.setWhatIfHover(null);
  const surface = h('div', {
    key: 'surface',
    onpointermove: pick,
    onpointerdown: pick,
    onpointerleave: leave,
    onpointercancel: leave,
    onpointerup: event => { if (event.pointerType !== 'mouse') leave(); },
    style: css({
      position: 'absolute', inset: compact ? '-10px 0' : 0, cursor: 'crosshair',
      /* A finger scrubs the chart instead of scrolling the page. */
      touchAction: compact ? 'none' : 'pan-y'
    })
  });

  const dot = (m, value, color, size, key) => h('div', {
    key,
    style: css({
      position: 'absolute', left: left(m), top: top(value), width: size + 'px', height: size + 'px',
      margin: `${-size / 2}px 0 0 ${-size / 2}px`, borderRadius: '50%', background: color,
      boxShadow: `0 0 0 3px ${C.card}`, pointerEvents: 'none'
    })
  });
  const vline = (m, border, key, opacity = 1) => h('div', {
    key,
    style: css({ position: 'absolute', top: 0, bottom: 0, left: left(m), width: 0, borderLeft: border, opacity, pointerEvents: 'none' })
  });

  const gridLines = grid.flatMap((v, i) => [
    h('div', {
      key: 'g' + i,
      style: css({
        position: 'absolute', left: 0, right: 0, top: top(v), height: 0,
        borderTop: v === 0 ? `1.5px solid ${C.ghost}` : `1px solid ${C.stroke}`
      })
    }),
    h('div', {
      key: 'l' + i,
      style: css({
        position: 'absolute', left: compact ? '-52px' : '-70px', width: compact ? '46px' : '60px', top: top(v),
        transform: 'translateY(-50%)', textAlign: 'right', fontFamily: MONO,
        fontSize: compact ? '9.5px' : '10.5px', color: v === 0 ? C.textDim : C.faint, whiteSpace: 'nowrap'
      })
    }, axis(v))
  ]);

  const xLabels = compact
    ? [0, Math.round(w.years / 2) * 12, months].filter((m, i, list) => list.indexOf(m) === i)
    : Array.from({ length: Math.floor(w.years / (w.years > 6 ? 2 : 1)) + 1 }, (_, i) => i * 12 * (w.years > 6 ? 2 : 1));
  const xAxis = h('div', {
    style: css({ position: 'relative', height: compact ? '16px' : '20px', margin: compact ? '0 4px 0 52px' : '0 28px 0 70px' })
  },
    ...xLabels.map(m => h('div', {
      key: 'x' + m,
      style: css({
        position: 'absolute', left: left(m), whiteSpace: 'nowrap', fontFamily: MONO,
        fontSize: compact ? '9.5px' : '10.5px', color: C.faint,
        transform: compact ? (m === 0 ? 'none' : m === months ? 'translateX(-100%)' : 'translateX(-50%)') : 'translateX(-50%)'
      })
    }, monthOf(m)))
  );

  // ------------------------------------------------------------ pieces ---
  const label = (text, color = C.faint) => h('div', {
    style: css({ fontFamily: MONO, fontSize: compact ? '9px' : '9.5px', letterSpacing: compact ? '1.1px' : '1.2px', color })
  }, text);
  const tile = (title, value, sub, { valueColor = C.text, highlight = false, titleColor } = {}) => h('div', {
    style: css({
      padding: compact ? '12px 14px' : '14px 16px', borderRadius: compact ? '14px' : '12px',
      border: `1px solid ${highlight ? C.accentDeep : C.stroke}`, background: highlight ? GAIN_BG : C.card,
      display: 'flex', flexDirection: 'column', gap: compact ? '5px' : '6px', minWidth: 0
    })
  },
    label(title, titleColor || (highlight ? GAIN_LABEL : C.faint)),
    h('div', {
      style: css({ fontFamily: MONO, fontSize: compact ? '17px' : '22px', fontWeight: 700, color: valueColor, whiteSpace: 'nowrap' })
    }, value),
    h('div', { style: css({ fontFamily: MONO, fontSize: compact ? '10px' : '11px', color: C.muted, lineHeight: 1.4 }) }, sub)
  );

  /* Short of cash because of the change, not as things already are. */
  const overdrawn = shortAt !== null && changed && base.short === null;
  const warning = shortAt !== null && h('div', {
    key: 'warn',
    role: 'alert',
    style: css({
      display: 'flex', alignItems: 'flex-start', gap: compact ? '10px' : '12px', padding: '12px 14px',
      border: `1px solid ${DANGER_LINE}`, borderRadius: compact ? '14px' : '12px', background: DANGER_BG
    })
  },
    h('div', { style: css({ width: '8px', height: '8px', flex: '0 0 8px', marginTop: '5px', borderRadius: '50%', background: C.danger }) }),
    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }) },
      h('div', { style: css({ fontSize: compact ? '12.5px' : '13px', fontWeight: 600, color: DANGER_TEXT }) }, s.wiShortTitle(monthOf(shortAt))),
      h('div', { style: css({ fontSize: compact ? '11.5px' : '12px', lineHeight: 1.5, color: DANGER_BODY }) },
        result.trim !== null ? s.wiShortTrim(s.wiPerMonthShort(money(result.trim))) : s.wiShortAlways)
    )
  );

  const active = WHAT_IF_PRESETS.findIndex(p => p.extra === w.extra && p.invest === w.invest && p.ret === w.ret);
  const presets = WHAT_IF_PRESETS.map((p, i) => h('button', {
    key: 'p' + i,
    type: 'button',
    title: s.wiPresetTitle(s.wiPerMonthShort(money(p.extra)), s.wiPerMonthShort(money(p.invest)), fmt.number(p.ret, 1) + '%'),
    'aria-pressed': active === i ? 'true' : 'false',
    onclick: () => actions.setWhatIfPreset(p),
    style: css(compact
      ? {
        flex: '1 1 0', minWidth: 0, height: '40px', border: 'none', borderRadius: '10px', cursor: 'pointer',
        background: active === i ? GAIN_BG : 'transparent', color: active === i ? C.accentHi : C.muted,
        fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600
      }
      : {
        height: '34px', padding: '0 14px', borderRadius: '17px', cursor: 'pointer', whiteSpace: 'nowrap',
        border: `1px solid ${active === i ? C.accent : C.stroke}`, background: active === i ? GAIN_BG : 'transparent',
        color: active === i ? C.accentHi : C.muted, fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600
      })
  }, s.wiPresets[i]));

  const perMonth = v => s.wiPerMonthShort(money(v));
  const sliders = [
    { key: 'extra', label: s.wiExtra, min: 0, max: 5000, step: 50, show: perMonth },
    { key: 'invest', label: s.wiInvest, min: 0, max: 5000, step: 50, show: perMonth },
    { key: 'ret', label: s.wiReturn, min: 0, max: 15, step: 0.5, show: v => fmt.number(v, 1) + '%' },
    { key: 'years', label: s.wiYears, min: 1, max: 10, step: 1, show: v => s.wiYearsValue(v) }
  ].map(({ key, label: name, min, max, step: by, show }) => {
    const value = w[key];
    const fill = pct(clamp((value - min) / (max - min), 0, 1));
    const height = compact ? 44 : 24;
    const trackTop = compact ? 20 : 10;
    const thumb = compact ? 24 : 18;
    return h('div', {
      key,
      style: css(compact
        ? { display: 'flex', flexDirection: 'column', gap: '4px', padding: '12px 16px 10px', borderBottom: key === 'years' ? 'none' : `1px solid ${C.line}` }
        : {
          display: 'flex', flexDirection: 'column', gap: '9px', padding: '14px 16px 12px',
          border: `1px solid ${C.stroke}`, borderRadius: '12px', background: C.card
        })
    },
      h('div', { style: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '10px' }) },
        h('div', { style: css({ fontSize: '13px', color: C.textDim }) }, name),
        h('div', { style: css({ fontFamily: MONO, fontSize: '14px', fontWeight: 700, color: C.text, whiteSpace: 'nowrap' }) }, show(value))
      ),
      h('div', { style: css({ position: 'relative', height: height + 'px' }) },
        h('div', { style: css({ position: 'absolute', left: 0, right: 0, top: trackTop + 'px', height: compact ? '5px' : '4px', borderRadius: '3px', background: C.strokeHi }) }),
        h('div', { style: css({ position: 'absolute', left: 0, top: trackTop + 'px', height: compact ? '5px' : '4px', borderRadius: '3px', background: C.accent, width: fill }) }),
        h('div', {
          style: css({
            position: 'absolute', top: (trackTop + (compact ? 2.5 : 2) - thumb / 2) + 'px', left: fill,
            width: thumb + 'px', height: thumb + 'px', marginLeft: -thumb / 2 + 'px', boxSizing: 'border-box',
            borderRadius: '50%', background: C.text, border: `${compact ? 4 : 3}px solid ${C.accent}`,
            boxShadow: '0 2px 6px rgba(0,0,0,.45)', pointerEvents: 'none'
          })
        }),
        /*
         * The real control, invisible on top: keyboard, screen readers and the
         * whole row as a target. Remade when a preset moves it, because a range
         * input keeps the value it was created with (see vdom.js).
         */
        h('input', {
          key: 'in' + state.whatIfRev,
          type: 'range', min: String(min), max: String(max), step: String(by), value: String(value),
          'aria-label': name, 'aria-valuetext': show(value),
          oninput: event => actions.setWhatIf(key, event.target.value),
          style: css({ position: 'absolute', inset: 0, width: '100%', height: height + 'px', margin: 0, opacity: 0, cursor: 'pointer' })
        })
      ),
      !compact && h('div', {
        key: 'range',
        style: css({ display: 'flex', justifyContent: 'space-between', fontFamily: MONO, fontSize: '10px', color: C.faint })
      }, h('span', null, show(min)), h('span', null, show(max)))
    );
  });

  // ------------------------------------------------------- composition ---
  const mixRow = (series, name, nameColor) => {
    const at = series.end;
    const debt = at.debt + Math.max(0, -at.cash);
    return { name, nameColor, debt, cash: Math.max(0, at.cash), rawCash: at.cash, inv: at.investments, net: at.netWorth };
  };
  const mix = [mixRow(scen, s.wiLegendChange, C.accent), mixRow(base, s.wiLegendNow, C.muted)];
  const side = Math.max(1, ...mix.map(r => Math.max(r.debt, r.cash + r.inv)));
  const bars = (r, height) => h('div', { style: css({ display: 'flex', height: height + 'px' }) },
    h('div', { style: css({ flex: '1 1 0', display: 'flex', justifyContent: 'flex-end' }) },
      h('div', { style: css({ height: height + 'px', borderRadius: '5px 0 0 5px', background: C.danger, width: pct(r.debt / side) }) })),
    h('div', { style: css({ width: '2px', background: C.ghost }) }),
    h('div', { style: css({ flex: '1 1 0', display: 'flex' }) },
      h('div', { style: css({ height: height + 'px', background: CASH, width: pct(r.cash / side) }) }),
      h('div', { style: css({ height: height + 'px', borderRadius: '0 5px 5px 0', background: C.accent, width: pct(r.inv / side) }) }))
  );
  const swatch = (color, text) => h('span', { style: css({ display: 'flex', alignItems: 'center', gap: '6px' }) },
    h('span', { style: css({ width: '10px', height: '10px', borderRadius: '2px', background: color }) }), text);
  const legendMix = h('div', { style: css({ display: 'flex', gap: compact ? '12px' : '14px', fontSize: compact ? '11px' : '11.5px', color: compact ? C.muted : C.textDim, flexWrap: 'wrap' }) },
    swatch(C.danger, s.wiMixDebt), swatch(CASH, s.wiMixCash), swatch(C.accent, s.wiMixInvest));
  const netColor = v => (v >= 0 ? C.accent : DANGER_TEXT);

  const composition = h('div', {
    style: css({
      padding: compact ? '14px 16px' : '16px 18px', border: `1px solid ${C.stroke}`, borderRadius: compact ? '16px' : '14px',
      background: C.card, display: 'flex', flexDirection: 'column', gap: compact ? '12px' : '14px'
    })
  },
    h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', justifyContent: compact ? 'space-between' : 'flex-start' }) },
      h('div', { style: css({ fontSize: compact ? '13.5px' : '14px', fontWeight: 700 }) }, s.wiMixTitle),
      h('div', { style: css({ fontFamily: MONO, fontSize: compact ? '9.5px' : '10.5px', color: C.faint }) }, s.wiMixAt(monthOf(months))),
      !compact && h('div', { key: 'sp', style: css({ flex: 1 }) }),
      !compact && h('div', { key: 'lg' }, legendMix)
    ),
    ...mix.map((r, i) => compact
      ? h('div', { key: 'm' + i, style: css({ display: 'flex', flexDirection: 'column', gap: '6px' }) },
        h('div', { style: css({ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }) },
          h('span', { style: css({ color: r.nameColor }) }, r.name),
          h('span', { style: css({ fontFamily: MONO, fontWeight: 700, color: netColor(r.net) }) }, money(r.net))),
        bars(r, 16),
        h('div', { style: css({ display: 'flex', justifyContent: 'space-between', fontFamily: MONO, fontSize: '10px', color: C.faint }) },
          h('span', null, s.wiMixDebtOf(money(r.debt))), h('span', null, s.wiMixAssetsShort(money(r.rawCash + r.inv)))))
      : h('div', { key: 'm' + i, style: css({ display: 'flex', flexDirection: 'column', gap: '6px' }) },
        h('div', { style: css({ display: 'grid', gridTemplateColumns: '92px minmax(0,1fr) 110px', gap: '14px', alignItems: 'center' }) },
          h('div', { style: css({ fontSize: '12.5px', color: r.nameColor }) }, r.name),
          bars(r, 22),
          h('div', { style: css({ textAlign: 'right', fontFamily: MONO, fontSize: '13px', fontWeight: 700, color: netColor(r.net), whiteSpace: 'nowrap' }) }, money(r.net))),
        h('div', { style: css({ display: 'grid', gridTemplateColumns: '92px minmax(0,1fr) 110px', gap: '14px' }) },
          h('div', null),
          h('div', { style: css({ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', fontFamily: MONO, fontSize: '10.5px', color: C.faint }) },
            h('span', null, s.wiMixDebtOf(money(r.debt))), h('span', null, s.wiMixAssets(money(r.rawCash), money(r.inv)))),
          h('div', null)))),
    compact && h('div', { key: 'lg' }, legendMix)
  );

  const note = text => h('div', {
    style: css({ fontSize: compact ? '11px' : '11.5px', lineHeight: 1.5, color: C.faint, padding: compact ? '0 4px' : '2px 4px' })
  }, text);

  const kpiDebt = hasDebt ? debtFree(scen.debtFree) : s.wiNoDebt;
  const kpiDebtBase = debtFree(base.debtFree);

  // ------------------------------------------------------------- phone ---
  if (compact) {
    const at = hover ?? months;
    const scenAt = scen.monthly[at].netWorth;
    const baseAt = base.monthly[at].netWorth;
    const diffAt = scenAt - baseAt;
    return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '12px' }) },
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '2px', padding: '0 2px' }) },
        h('div', { style: css({ fontSize: '17px', fontWeight: 800, letterSpacing: '-0.3px' }) }, s.wiTitle),
        h('div', { style: css({ fontSize: '11.5px', color: C.muted }) }, s.wiSubShort)
      ),
      h('div', {
        style: css({
          padding: '14px 16px', border: `1px solid ${C.stroke}`, borderRadius: '16px', background: C.card,
          display: 'flex', flexDirection: 'column', gap: '10px'
        })
      },
        h('div', { style: css({ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px' }) },
          label(hover !== null ? monthOf(at).toUpperCase() : s.wiKpiEnd(years)),
          h('div', { style: css({ fontFamily: MONO, fontSize: '10px', color: C.faint }) }, hover !== null ? s.wiTouching : s.wiTouch)
        ),
        h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }) },
          h('div', {
            style: css({ fontFamily: MONO, fontSize: '26px', fontWeight: 700, letterSpacing: '-0.5px', color: scenAt >= 0 ? C.accent : C.text })
          }, money(scenAt)),
          changed && h('div', {
            key: 'chip',
            style: css({
              padding: '3px 8px', borderRadius: '8px', background: diffAt >= 0 ? GAIN_BG : DANGER_BG,
              fontFamily: MONO, fontSize: '11.5px', fontWeight: 700, color: diffAt >= 0 ? C.accent : DANGER_TEXT
            })
          }, signed(diffAt))
        ),
        changed && h('div', { key: 'base', style: css({ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: C.muted }) },
          h('span', { style: css({ width: '14px', height: 0, borderTop: `2px dashed ${C.muted}` }) }),
          h('span', null, s.wiLegendNow),
          h('span', { style: css({ fontFamily: MONO, color: C.textDim }) }, money(baseAt))
        ),
        h('div', { style: css({ position: 'relative', height: '200px', margin: '8px 4px 0 52px' }) },
          ...gridLines,
          svg,
          ...markers.flatMap((k, i) => [
            vline(k.m, `1px dashed ${k.color}`, 'mv' + i, 0.8),
            h('div', {
              key: 'md' + i,
              style: css({
                position: 'absolute', top: '-4px', left: left(k.m), width: '9px', height: '9px', marginLeft: '-4.5px',
                borderRadius: '50%', background: k.color, boxShadow: `0 0 0 3px ${C.card}`
              })
            })
          ]),
          vline(at, `1px solid ${C.muted}`, 'cur'),
          changed && dot(at, baseAt, C.muted, 9, 'cb'),
          dot(at, scenAt, changed ? C.accent : C.muted, 12, 'cs'),
          surface
        ),
        xAxis,
        markers.length > 0 && h('div', { key: 'marks', style: css({ display: 'flex', flexDirection: 'column', gap: '7px', paddingTop: '4px' }) },
          ...markers.map((k, i) => h('div', { key: 'k' + i, style: css({ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '12px' }) },
            h('span', { style: css({ width: '8px', height: '8px', flex: '0 0 8px', borderRadius: '50%', background: k.color }) }),
            h('span', { style: css({ flex: 1, minWidth: 0, color: C.textDim }) }, k.name),
            h('span', { style: css({ fontFamily: MONO, fontSize: '11.5px', color: k.fg }) }, monthOf(k.m))
          )))
      ),
      warning,
      h('div', { style: css({ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }) },
        tile(s.wiKpiDebt, kpiDebt, hasDebt && changed ? s.wiInstead(kpiDebtBase) : ' '),
        tile(s.wiKpiLessInterest, money(Math.max(0, result.interestSaved)),
          changed ? s.wiAboutMonthShort(signed(gain / months)) : ' ', { valueColor: C.accent })
      ),
      h('div', {
        style: css({ display: 'flex', padding: '4px', gap: '4px', border: `1px solid ${C.stroke}`, borderRadius: '14px', background: C.card })
      }, ...presets),
      h('div', {
        style: css({ border: `1px solid ${C.stroke}`, borderRadius: '16px', background: C.card, display: 'flex', flexDirection: 'column' })
      }, ...sliders),
      composition,
      note(s.wiQueue + ' ' + s.wiNote)
    );
  }

  // ----------------------------------------------------------- desktop ---
  const tooltip = hover !== null && (() => {
    const scenAt = scen.monthly[hover].netWorth;
    const baseAt = base.monthly[hover].netWorth;
    const diff = scenAt - baseAt;
    const ratio = hover / months;
    const row = (name, nameColor, value, valueStyle) => h('div', {
      style: css({ display: 'flex', justifyContent: 'space-between', gap: '14px', fontSize: '12px' })
    }, h('span', { style: css({ color: nameColor }) }, name), h('span', { style: css({ fontFamily: MONO, ...valueStyle }) }, value));
    return [
      vline(hover, `1px solid ${C.faint}`, 'hl'),
      changed && dot(hover, baseAt, C.muted, 9, 'hb'),
      dot(hover, scenAt, changed ? C.accent : C.muted, 11, 'hs'),
      h('div', {
        key: 'tip',
        style: css({
          position: 'absolute', top: '28px', left: left(hover),
          transform: ratio > 0.62 ? 'translateX(calc(-100% - 14px))' : 'translateX(14px)',
          minWidth: '196px', padding: '11px 13px', borderRadius: '10px', background: C.bg,
          border: `1px solid ${C.strokeHi}`, boxShadow: '0 12px 28px rgba(0,0,0,.45)',
          display: 'flex', flexDirection: 'column', gap: '7px', pointerEvents: 'none', zIndex: 2
        })
      },
        h('div', { style: css({ fontFamily: MONO, fontSize: '10px', letterSpacing: '1.2px', color: C.muted }) }, monthOf(hover).toUpperCase()),
        changed
          ? [
            row(s.wiLegendChange, C.accent, money(scenAt), { color: C.text }),
            row(s.wiLegendNow, C.muted, money(baseAt), { color: C.textDim }),
            h('div', { key: 'rule', style: css({ height: '1px', background: C.stroke }) }),
            row(s.wiDiff, C.muted, signed(diff), { fontWeight: 700, color: diff >= 0 ? C.accent : C.danger })
          ]
          : row(s.wiChart, C.muted, money(baseAt), { color: C.text })
      )
    ];
  })();

  const legendLine = (style, text) => h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', color: C.textDim }) },
    h('span', { style: css(style) }), text);

  return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '18px' }) },
    h('div', { style: css({ display: 'flex', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap' }) },
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }) },
        h('div', { style: css({ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.5px' }) }, s.wiTitle),
        h('div', { style: css({ fontSize: '13px', color: C.muted }) }, s.wiSub)
      ),
      h('div', { style: css({ flex: 1 }) }),
      h('div', { style: css({ display: 'flex', gap: '6px', flexWrap: 'wrap' }) }, ...presets)
    ),
    h('div', { style: css({ display: 'flex', flexWrap: 'wrap', gap: '18px', alignItems: 'flex-start' }) },
      h('div', { style: css({ flex: '1 1 260px', maxWidth: '340px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '10px' }) },
        ...sliders,
        note(s.wiQueue)
      ),
      h('div', { style: css({ flex: '999 1 520px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '14px' }) },
        h('div', { style: css({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }) },
          tile(s.wiKpiEnd(years), money(end.scen), s.wiWithout(money(end.base)), { valueColor: end.scen >= 0 ? C.accent : C.text }),
          /*
           * When the sliders drive cash below zero, the gain is partly money the
           * plan does not have -- said on the tile itself, not only in the
           * warning underneath, and no longer in celebratory green.
           */
          overdrawn
            ? tile(s.wiKpiGain, signed(gain), s.wiGainOverdrawn, { valueColor: DANGER_TEXT, titleColor: DANGER_TEXT })
            : tile(s.wiKpiGain, signed(gain), changed ? s.wiAboutMonth(signed(gain / months)) : s.wiHint,
              { valueColor: gain >= 0 ? C.accent : C.danger, highlight: true }),
          tile(s.wiKpiDebt, kpiDebt, hasDebt ? s.wiWithout(kpiDebtBase) : ' '),
          tile(s.wiKpiInterest, money(Math.max(0, result.interestSaved)),
            s.wiInterestInstead(money(scen.interest), money(base.interest)))
        ),
        warning,
        h('div', {
          style: css({
            padding: '16px 18px 12px', border: `1px solid ${C.stroke}`, borderRadius: '14px', background: C.card,
            display: 'flex', flexDirection: 'column', gap: '12px'
          })
        },
          h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap' }) },
            h('div', { style: css({ fontSize: '14px', fontWeight: 700 }) }, s.wiChart),
            changed && legendLine({ width: '18px', height: 0, borderTop: `2.5px solid ${C.accent}` }, s.wiLegendChange),
            legendLine({ width: '18px', height: 0, borderTop: `2px ${changed ? 'dashed' : 'solid'} ${C.muted}` }, s.wiLegendNow),
            changed && legendLine({ width: '14px', height: '10px', borderRadius: '2px', background: 'rgba(61,217,160,.18)' }, s.wiLegendDiff),
            h('div', { style: css({ flex: 1 }) }),
            h('div', { style: css({ fontFamily: MONO, fontSize: '10.5px', color: C.faint }) }, s.wiHover)
          ),
          h('div', {
            style: css({ position: 'relative', height: '320px', margin: (markers.length > 1 ? 64 : 38) + 'px 28px 0 70px' })
          },
            ...gridLines,
            svg,
            ...markers.flatMap((k, i) => {
              const ratio = k.m / months;
              return [
                vline(k.m, `1px dashed ${k.color}`, 'mv' + i, 0.75),
                h('div', {
                  key: 'mt' + i,
                  style: css({
                    position: 'absolute', top: (i % 2 === 0 ? -8 : -34) + 'px', left: left(k.m),
                    transform: ratio > 0.8 ? 'translate(-100%,-100%)' : ratio < 0.12 ? 'translate(0,-100%)' : 'translate(-50%,-100%)',
                    padding: '3px 8px', borderRadius: '6px', background: k.bg, border: `1px solid ${k.color}`,
                    fontFamily: MONO, fontSize: '10px', color: k.fg, whiteSpace: 'nowrap', pointerEvents: 'none'
                  })
                }, k.tag)
              ];
            }),
            changed && dot(months, end.base, C.muted, 9, 'eb'),
            dot(months, changed ? end.scen : end.base, changed ? C.accent : C.muted, 9, 'es'),
            ...(tooltip || []),
            surface
          ),
          xAxis
        ),
        composition,
        note(s.wiNote)
      )
    )
  );
}
